<template>
  <v-card
    v-if="provider"
    data-cy="selection-card"
    border
    flat
    tile
  >
    <v-row>
      <v-col>
        <template v-if="ordered || !hideToggle">
          <v-row class="px-3 py-3 d-flex align-center">
            <v-col class="d-flex align-center">
              <template v-if="ordered">
                <template v-if="readonly">
                  <SelectionRanking :index="index" />
                </template>
                <template v-else>
                  <v-btn
                    @click.stop.prevent="$emit('move', -1)"
                    :aria-label="$bct('Move preference up in your list')"
                    :disabled="!(index > 0)"
                    class="focus-very-visible me-3"
                    color="primary"
                    data-cy="selection-down"
                    data-testid="selection-down-button"
                    icon="expand_less"
                    size="small"
                    variant="outlined"
                  />
                  <SelectionRanking :index="index" />
                  <v-btn
                    @click.stop.prevent="$emit('move', 1)"
                    :aria-label="$bct('Move preference down in your list')"
                    :disabled="!(length > index + 1)"
                    class="ms-3 focus-very-visible"
                    color="primary"
                    data-cy="selection-up"
                    data-testid="selection-up-button"
                    icon="expand_more"
                    size="small"
                    variant="outlined"
                  />
                </template>
              </template>
            </v-col>

            <v-col
              v-if="!readonly && !hideToggle"
              class="d-flex justify-end"
              cols="4"
            >
              <v-btn
                @click="$emit('toggle', false)"
                color="red"
                size="small"
              >
                {{ $bct('Remove') }}
              </v-btn>
            </v-col>
          </v-row>

          <v-divider />
        </template>

        <div class="c-black pa-3">
          <v-row
            v-if="provider.accepts_subsidy || provider.closed_status != 'Open'"
            class="mb-2"
            dense
          >
            <v-col>
              <v-chip
                v-if="provider.closed_status && provider.closed_status != 'Open'"
                class="me-2"
                color="red"
              >
                <v-icon
                  class="c-white fs-16 me-2"
                  icon="warning"
                  size="24"
                />
                <span class="fw-600">
                  {{ $bct(provider.closed_status) }}
                </span>
              </v-chip>
              <v-chip
                v-if="provider.accepts_subsidy"
                color="secondary"
                variant="outlined"
              >
                {{ $bct('Accepts Financial Aid') }}
              </v-chip>
            </v-col>
          </v-row>

          <v-row
            class="ma-0 pa-0 d-flex align-center"
            dense
          >
            <v-col>
              <h4
                class="c-black fw-600 fs-20"
                data-cy="selection-name"
                dense
              >
                {{ provider.name }}
              </h4>
              <div
                v-if="provider.license_business_name != provider.name"
                class="fs-16"
              >
                {{ provider.license_business_name }}
              </div>
            </v-col>
          </v-row>

          <v-row
            class="ma-0 pa-0"
            dense
          >
            <v-col>
              <div class="fs-16 fw-400">
                {{ providerAddress() }}
              </div>
            </v-col>
          </v-row>

          <FavoriteProviderSummary
            v-if="$store.state.schemas[provider.schema_id]?.meta?.card?.enable_summary"
            :provider="provider"
          />

          <div v-if="$role == 'specialist'">
            <div
              v-for="(opening, openingsIndex) in openings"
              v-show="opening.count > 0"
              :key="openingsIndex"
              class="align-center b-1 bc-extra-light-gray d-flex my-3 pa-4"
            >
              <v-icon
                class="c-green me-2"
                icon="check_circle"
                size="24"
              />
              <div class="d-inline-block fs-16 fw-500">
                {{ $bct('Openings available for') }}
              </div>
              <div class="d-inline-block fs-16 fw-500 ms-1 fw-600">
                {{ monthsToAgeString(opening.min) }} - {{ monthsToAgeString(opening.max) }}
              </div>
              <v-spacer />
              <v-btn
                @click="reserve(opening)"
                color="primary"
              >
                {{ $bct('Match') }}
              </v-btn>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-card>
</template>

<script setup>
import FavoriteProviderSummary from '@/parent/components/favorites/FavoriteProviderSummary.vue';
import { formatAddress } from '@/shared/services/address';
import SelectionRanking from '@/shared/components/subsidy/SelectionRanking.vue';
import useApi from '@/shared/composables/useApi';

const { api: Api } = useApi();
const $bct = inject('translator');

const props = defineProps({
  active: {
    type: Boolean,
    default: false,
  },
  hideToggle: {
    type: Boolean,
    default: false,
  },
  index: {
    type: Number,
    default: null,
  },
  length: {
    type: Number,
    default: null,
  },
  match: {
    type: Object,
    default: null,
  },
  ordered: {
    type: Boolean,
    default: false,
  },
  providerId: {
    type: String,
    default: null,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['move', 'toggle']);

const openings = ref([]);
const provider = ref(null);

onMounted(load);

function load() {
  if (!props.match && props.providerId) {
    Api.publicApi.provider.get(props.providerId, (resp) => {
      provider.value = resp.data;
      openings.value = provider.value.vacancies;
    });
  } else {
    provider.value = props.match;
    openings.value = props.match.openings;
  }
}

function providerAddress() {
  if (!provider.value) return null;

  return formatAddress(provider.value.address1, provider.value.city, provider.value.zip);
}
</script>
